/**
 * Firebase client init for televisit-app (TV-P2-01+).
 *
 * Only the bits we actually use are initialized:
 *   - Auth (custom-token sign-in, exchanged from the Keycloak JWT
 *     via the `exchangeKeycloakToken` Cloud Function).
 *   - Firestore (realtime queue listeners under
 *     `televisit-queue/{tenantId}/patients/{patientId}`).
 *   - Functions (callable handle for the token exchange).
 *
 * All other Firebase SDKs (Storage, Database, Messaging, …) are
 * intentionally omitted. Smaller bundle, smaller surface.
 *
 * Config source: hardcoded for the `collabkare-250512` project,
 * matching the pattern in collabkarebusiness/src/auth/firebaseConfig.js.
 * Firebase web config IS public — apiKey/projectId/appId in a browser
 * bundle is fine per Google's docs; all access control is enforced by
 * Firestore/Storage rules + Auth. Env-var overrides are honored when
 * present (lets a future staging build point at a different project).
 *
 * The init is wrapped in try/catch so a misconfigured environment
 * surfaces a console error but never throws synchronously at module
 * load — that previously crashed the React tree before any error
 * boundary could render.
 */

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY
    || 'AIzaSyBmzPJF-LdrKzba3u4pt5PLLMpqOn-SpH0',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN
    || 'collabkare-250512.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID
    || 'collabkare-250512',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET
    || 'collabkare-250512.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID
    || '871290887238',
  appId: import.meta.env.VITE_FIREBASE_APP_ID
    || '1:871290887238:web:28402f0a92f723e78d3e47',
};

let app = null;
let firebaseAuth = null;
let firestore = null;
let firebaseFunctions = null;

try {
  // Avoid double-init under Vite HMR / React StrictMode — both
  // re-evaluate this module multiple times in dev.
  app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  firebaseAuth = getAuth(app);
  firestore = getFirestore(app);
  firebaseFunctions = getFunctions(app, 'us-central1');
} catch (err) {
  // A bad config (missing apiKey, malformed projectId) would otherwise
  // throw synchronously at module load — the React tree never paints.
  // Log loudly + leave the handles null so callers can no-op gracefully.
  // eslint-disable-next-line no-console
  console.error(
    '[Firebase] initializeApp failed — realtime features disabled. ' +
      'Check VITE_FIREBASE_* env vars or fall back to the hardcoded defaults.',
    err,
  );
}

export { firebaseAuth, firestore, firebaseFunctions };
export default app;
